import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { directLinkStateSelector } from '@src/stores/directLink';
import { AUTH_SUB_STEP, type AuthSubStep } from '@src/views/checkout/authSubStep';
import { resolveCheckoutBackTarget } from '@src/views/checkout/core/resolveCheckoutBackTarget';
import type { CheckoutMode } from '@src/views/checkout/types';

export type CheckoutFlowContextValue = {
  mode: CheckoutMode;
  goTo: (mode: CheckoutMode) => void;
  authSubStep: AuthSubStep;
  setAuthSubStep: (step: AuthSubStep) => void;
  goBack: () => void;
  /** Whether back leads anywhere from this step; the header hides the button when it does not. */
  canGoBack: boolean;
  bootstrapping: boolean;
};

export const CheckoutFlowContext = createContext<CheckoutFlowContextValue | null>(null);

type CheckoutFlowProviderProps = {
  mode: CheckoutMode;
  goTo: (mode: CheckoutMode) => void;
  initialAuthSubStep?: AuthSubStep;
  bootstrapping?: boolean;
  children: ReactNode;
};

export const CheckoutFlowProvider = (props: CheckoutFlowProviderProps) => {
  const { mode, goTo, initialAuthSubStep = AUTH_SUB_STEP.IDENTIFICATION, bootstrapping = false, children } = props;
  const [authSubStep, setAuthSubStep] = useState<AuthSubStep>(initialAuthSubStep);
  const dispatch = useAppDispatch();
  const continueShoppingPage = useWidgetParam('continue-shopping-page');
  const { directLink } = useAppSelector(directLinkStateSelector);

  const target = useMemo(
    () => resolveCheckoutBackTarget(mode, authSubStep, { canLeave: !directLink }),
    [authSubStep, directLink, mode],
  );

  const goBack = useCallback(() => {
    if (target.type === 'none') {
      return;
    }

    if (target.type === 'previousPage') {
      // Nothing behind a tab opened straight on the checkout: `back()` would be a dead button.
      if (window.history.length > 1) {
        window.history.back();
        return;
      }

      dispatch(changeLocation(continueShoppingPage || '/'));
      return;
    }

    if (target.type === 'continueShopping') {
      dispatch(changeLocation(continueShoppingPage || '/'));
      return;
    }

    if (target.type === 'mode') {
      goTo(target.mode);
      return;
    }

    setAuthSubStep(target.step);
  }, [continueShoppingPage, dispatch, goTo, target]);

  const value = useMemo(
    () => ({
      mode,
      goTo,
      authSubStep,
      setAuthSubStep,
      goBack,
      canGoBack: target.type !== 'none',
      bootstrapping,
    }),
    [authSubStep, bootstrapping, goBack, goTo, mode, target],
  );

  return <CheckoutFlowContext.Provider value={value}>{children}</CheckoutFlowContext.Provider>;
};

export const useCheckoutNavigation = () => {
  const context = useContext(CheckoutFlowContext);

  if (!context) {
    throw new Error('useCheckoutNavigation must be used within CheckoutFlowProvider');
  }

  return context;
};
